import React, { useEffect, useState } from "react";
import { X, GitCompare } from "lucide-react";
import { getDetail } from "@/lib/api";
import { sigMeta, fmt, cur, pct, UP, DOWN } from "@/lib/format";

export default function CompareTray({ tickers, onRemove, onSelect }) {
  const [data, setData] = useState({});
  useEffect(() => {
    tickers.forEach((t) => {
      if (!data[t]) getDetail(t).then((d) => setData((p) => ({ ...p, [t]: d }))).catch(() => {});
    });
  }, [tickers]); // eslint-disable-line

  if (!tickers.length) return null;
  const Row = ({ label, render }) => (
    <div className="flex items-center gap-2 text-[12px]">
      <span className="w-20 shrink-0 text-slate-400 uppercase text-[10px] tracking-wide">{label}</span>
      {tickers.map((t) => <div key={t} className="flex-1 min-w-[90px] font-mono">{render(data[t])}</div>)}
    </div>
  );

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-[4px] shadow-xs p-3.5" data-testid="compare-tray">
      <div className="flex items-center gap-2 mb-3">
        <GitCompare size={15} className="text-[#0070BA]" />
        <span className="font-bold text-[13px] tracking-[0.1em] uppercase text-slate-700">Compare</span>
        <span className="font-mono text-[11px] text-slate-400">{tickers.length}/3</span>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="w-20 shrink-0" />
          {tickers.map((t) => {
            const d = data[t]; const m = sigMeta(d?.signal || "NEUTRAL");
            return (
              <div key={t} className="flex-1 min-w-[90px] flex items-center justify-between gap-1">
                <button onClick={() => onSelect(t)} data-testid={`compare-open-${t}`} className="font-bold text-[13px] text-slate-900 truncate hover:text-[#0070BA]">{t.replace(".NS", "").replace("^", "")}</button>
                <button onClick={() => onRemove(t)} data-testid={`compare-remove-${t}`} className="text-slate-300 hover:text-[#D32F2F]"><X size={13} /></button>
              </div>
            );
          })}
        </div>
        <Row label="Signal" render={(d) => { const m = sigMeta(d?.signal || "NEUTRAL"); return <span className="font-semibold text-[10px] px-1.5 py-0.5 rounded-[3px] border" style={{ color: m.color, background: m.bg, borderColor: m.border }}>{m.label}</span>; }} />
        <Row label="Price" render={(d) => d ? <span className="text-slate-800">{cur(d.market)}{fmt(d.price)}</span> : "—"} />
        <Row label="1W %" render={(d) => d ? <span style={{ color: (d.change_pct ?? 0) >= 0 ? UP : DOWN }}>{pct(d.change_pct)}</span> : "—"} />
        <Row label="RSI" render={(d) => d ? <span style={{ color: d.rsi > 80 ? DOWN : d.rsi < 28 ? UP : "#334155" }}>{fmt(d.rsi, 1)}</span> : "—"} />
        <Row label="ADX" render={(d) => d ? <span style={{ color: (d.adx ?? 0) >= 25 ? (d.adx_direction === "BULL" ? UP : DOWN) : "#64748B" }}>{fmt(d.adx, 0)} {d.adx_direction || ""}</span> : "—"} />
        <Row label="P/E" render={(d) => d ? <span className="text-slate-600">{fmt(d.current_pe, 1)}</span> : "—"} />
        <Row label="5Y Dev" render={(d) => d ? <span style={{ color: (d.pe_dev_pct ?? 0) >= 50 ? DOWN : (d.pe_dev_pct ?? 0) <= -50 ? UP : "#64748B" }}>{pct(d.pe_dev_pct)}</span> : "—"} />
        <Row label="ROE" render={(d) => d ? <span className="text-slate-600">{fmt(d.roe, 1)}%</span> : "—"} />
      </div>
    </div>
  );
}
